import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { messagesAPI } from '../utils/api';
import { useSocket } from '../context/SocketContext';
import Avatar from './Avatar';
import toast from 'react-hot-toast';

const GroupAvatar = ({ name, size = 40 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.35, flexShrink: 0 }}>
    {name?.charAt(0)?.toUpperCase() || 'G'}
  </div>
);

export default function ChatWindow({ conversation, currentUser }) {
  const { sendMessage, startTyping, stopTyping, markMessagesRead, on, off, isUserOnline } = useSocket();

  const isGroup = conversation.isGroup;
  const otherUser = !isGroup ? (conversation.otherUser || conversation.participants?.find(p => p._id !== currentUser._id)) : null;
  const groupParticipants = isGroup ? (conversation.participants || []) : [];
  const groupParticipantIds = groupParticipants.map(p => p._id || p).filter(Boolean);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [typingTimer, setTypingTimer] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const isOnline = !isGroup && isUserOnline(otherUser?._id);
  const onlineGroupCount = isGroup ? groupParticipantIds.filter(id => id !== currentUser._id && isUserOnline(id)).length : 0;

  const loadMessages = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await messagesAPI.getMessages(conversation._id, { page: pg, limit: 50 });
      const { messages: msgs, pagination } = res.data.data;
      if (pg === 1) { setMessages(msgs); scrollDown(); }
      else setMessages(prev => [...msgs, ...prev]);
      setHasMore(pg < pagination.pages);
      setPage(pg);
    } catch { toast.error('Failed to load messages.'); }
    finally { setLoading(false); }
  }, [conversation._id]);

  useEffect(() => { loadMessages(1); }, [loadMessages]);

  useEffect(() => {
    if (!isGroup && otherUser?._id) markMessagesRead(conversation._id, otherUser._id);
  }, [conversation._id, isGroup, otherUser?._id, markMessagesRead]);

  useEffect(() => {
    const onReceived = (msg) => {
      if (msg.conversationId === conversation._id) {
        setMessages(p => [...p, msg]);
        if (!isGroup && otherUser?._id) markMessagesRead(conversation._id, otherUser._id);
        scrollDown();
      }
    };
    const onSent = (msg) => {
      if (msg.conversationId === conversation._id)
        setMessages(p => [...p.filter(m => m._id !== msg._id), msg]);
    };
    const onRead = ({ conversationId }) => {
      if (conversationId === conversation._id)
        setMessages(p => p.map(m => ({ ...m, isRead: true })));
    };
    const onTypStart = ({ userId, name, conversationId }) => {
      if (conversationId === conversation._id && userId !== currentUser._id)
        setTypingUsers(prev => prev.find(u => u.id === userId) ? prev : [...prev, { id: userId, name }]);
    };
    const onTypStop = ({ userId, conversationId }) => {
      if (conversationId === conversation._id)
        setTypingUsers(prev => prev.filter(u => u.id !== userId));
    };

    on('message:received', onReceived);
    on('message:sent', onSent);
    on('messages:read-receipt', onRead);
    on('typing:start', onTypStart);
    on('typing:stop', onTypStop);
    return () => {
      off('message:received', onReceived);
      off('message:sent', onSent);
      off('messages:read-receipt', onRead);
      off('typing:start', onTypStart);
      off('typing:stop', onTypStop);
    };
  }, [conversation._id, isGroup, otherUser?._id, currentUser._id, on, off, markMessagesRead]);

  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);

  const handleInput = e => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    if (isGroup) {
      groupParticipantIds.filter(id => id !== currentUser._id).forEach(id => startTyping(id, conversation._id));
    } else if (otherUser?._id) {
      startTyping(otherUser._id, conversation._id);
    }
    clearTimeout(typingTimer);
    setTypingTimer(setTimeout(() => {
      if (isGroup) groupParticipantIds.filter(id => id !== currentUser._id).forEach(id => stopTyping(id, conversation._id));
      else if (otherUser?._id) stopTyping(otherUser._id, conversation._id);
    }, 1500));
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const tempId = `temp_${Date.now()}`;
    const optimistic = { _id: tempId, sender: { _id: currentUser._id, name: currentUser.name, avatar: currentUser.avatar }, receiver: isGroup ? null : otherUser?._id, conversationId: conversation._id, content, isRead: false, createdAt: new Date().toISOString(), sending: true };
    setMessages(p => [...p, optimistic]);
    scrollDown();

    const sent = sendMessage({ conversationId: conversation._id, receiverId: isGroup ? null : otherUser?._id, content }, res => {
      if (!res?.success) { setMessages(p => p.filter(m => m._id !== tempId)); toast.error('Failed to send.'); }
      else setMessages(p => p.map(m => m._id === tempId ? { ...res.data, sending: false } : m));
    });

    if (!sent) {
      try {
        const res = await messagesAPI.sendMessage({ conversationId: conversation._id, receiverId: isGroup ? null : otherUser?._id, content });
        setMessages(p => p.map(m => m._id === tempId ? { ...res.data.data, sending: false } : m));
      } catch { setMessages(p => p.filter(m => m._id !== tempId)); toast.error('Failed to send.'); }
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const fmtDate = d => {
    const dt = new Date(d);
    if (isToday(dt)) return 'Today';
    if (isYesterday(dt)) return 'Yesterday';
    return format(dt, 'EEEE, d MMMM yyyy');
  };

  const fmtStatus = () => {
    if (typingUsers.length) return null;
    if (isGroup) return `${groupParticipantIds.length} members · ${onlineGroupCount} online`;
    if (isOnline) return <><span className="dot"></span>Online</>;
    if (otherUser?.lastSeen) return `Last seen ${isToday(new Date(otherUser.lastSeen)) ? format(new Date(otherUser.lastSeen), 'HH:mm') : format(new Date(otherUser.lastSeen), 'd MMM')}`;
    return 'Offline';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="chat-header">
        {isGroup ? <GroupAvatar name={conversation.groupName} size={40} /> : <Avatar user={otherUser} size="md" showStatus isOnline={isOnline} />}
        <div className="chat-header-info">
          <div className="chat-header-name">
            {isGroup ? conversation.groupName : otherUser?.name}
            {isGroup && <span style={{ fontSize: 11, background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: 4, padding: '1px 6px', marginLeft: 8, fontWeight: 600 }}>GROUP</span>}
          </div>
          <div className={`chat-header-status${!isGroup && isOnline ? ' online' : ''}`}>
            {typingUsers.length > 0
              ? <><span className="dot" style={{ background: 'var(--brand)' }}></span><span style={{ color: 'var(--brand)' }}>{typingUsers.length === 1 ? `${typingUsers[0].name} is typing…` : `${typingUsers.length} people are typing…`}</span></>
              : fmtStatus()
            }
          </div>
        </div>
        {!isGroup && <span className="chat-header-badge">{otherUser?.department}</span>}
        {isGroup && <span className="chat-header-badge">{groupParticipantIds.length} members</span>}
      </div>

      <div className="chat-messages">
        {hasMore && <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'center', marginBottom: 12 }} onClick={() => loadMessages(page + 1)}>Load older messages</button>}
        {loading && messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px', borderTopColor: 'var(--brand)', borderColor: 'var(--border)' }} />Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', margin: 'auto', color: 'var(--text-3)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>{isGroup ? '👥' : '👋'}</div>
            <div style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>{isGroup ? `Welcome to ${conversation.groupName}` : 'Start your conversation'}</div>
            <div style={{ fontSize: 13 }}>{isGroup ? `${groupParticipantIds.length} members in this group` : `Say hello to ${otherUser?.name}`}</div>
          </div>
        ) : (() => {
          const rows = [];
          messages.forEach((msg, idx) => {
            const prev = messages[idx - 1];
            const msgDate = new Date(msg.createdAt);
            const showDate = !prev || !isSameDay(msgDate, new Date(prev.createdAt));
            const isMine = (msg.sender?._id || msg.sender) === currentUser._id;
            const isCont = prev && (prev.sender?._id || prev.sender) === (msg.sender?._id || msg.sender) && !showDate && (msgDate - new Date(prev.createdAt)) < 120000;

            if (showDate) rows.push(<div key={`d_${msg._id}`} className="date-divider"><span>{fmtDate(msg.createdAt)}</span></div>);

            rows.push(
              <div key={msg._id} className={`msg-row${isMine ? ' own' : ''}${isCont ? ' continuation' : ''}`}>
                {!isMine && !isCont && (isGroup ? <Avatar user={msg.sender} size="sm" /> : <Avatar user={otherUser} size="sm" />)}
                {!isMine && isCont && <div style={{ width: 34, flexShrink: 0 }} />}
                <div className="msg-group">
                  {!isMine && !isCont && isGroup && <div className="msg-sender">{msg.sender?.name}</div>}
                  {!isMine && !isCont && !isGroup && <div className="msg-sender">{msg.sender?.name}</div>}
                  <div className={`msg-bubble${isMine ? ' sent' : ' received'}${msg.sending ? ' sending' : ''}${msg.isDeleted ? ' deleted' : ''}`}>
                    {msg.content}
                    {msg.isBroadcast && !isMine && <span style={{ display: 'block', fontSize: 10, opacity: .65, marginTop: 3 }}>📢 Broadcast</span>}
                  </div>
                  {!isCont && (
                    <div className="msg-meta">
                      <span className="msg-time">{format(new Date(msg.createdAt), 'HH:mm')}</span>
                      {isMine && <span className={`msg-status${msg.isRead ? ' read' : ''}`}>{msg.sending ? '🕐' : msg.isRead ? '✓✓' : '✓'}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          });
          return rows;
        })()}

        {typingUsers.length > 0 && (
          <div className="typing-indicator">
            {isGroup ? <Avatar user={typingUsers[0]} size="sm" /> : <Avatar user={otherUser} size="sm" />}
            <div style={{ background: '#fff', padding: '10px 14px', borderRadius: 16, borderBottomLeftRadius: 4, boxShadow: 'var(--shadow-sm)' }}>
              <div className="typing-dots"><span /><span /><span /></div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-wrap">
        <div className="chat-input-box">
          <textarea ref={textareaRef} placeholder={isGroup ? `Message ${conversation.groupName}…` : `Message ${otherUser?.name}…`} value={input} onChange={handleInput} onKeyDown={handleKey} rows={1} />
          <button className="chat-send-btn" onClick={handleSend} disabled={!input.trim() || sending} title="Send (Enter)">➤</button>
        </div>
        <div className="chat-input-hint">Enter to send · Shift+Enter for new line</div>
      </div>
    </div>
  );
}
